import { useMemo } from "react";
import { MapPin, X, Music } from "lucide-react";
import { BEYOND, MUSIC_CATEGORIES, countByRegion, visibleRegions, visibleCities } from "./useRegion";

// Region → City picker shared by the home map and /events. Always keeps the OTHER regions one tap away.
export const RegionBar = ({ events = [], regions: allRegions = [], sel, setSel, compact = false, testId = "region-bar", extra = null }) => {
  const regions = useMemo(() => visibleRegions(allRegions), [allRegions]);
  const { counts, cities } = useMemo(() => countByRegion(events, allRegions), [events, allRegions]);
  const active = sel.region !== "all" ? (sel.region === BEYOND.id ? BEYOND : regions.find((r) => r.id === sel.region)) : null;
  const musicElsewhere = useMemo(
    () => (active ? events.filter((e) => MUSIC_CATEGORIES.has(e.category) && !(active.cities || []).some((c) => c.toLowerCase() === (e.city || "").toLowerCase())).length : 0),
    [events, active]
  );
  if (!allRegions.length) return null;

  const pill = (id, label, color, count, long) => {
    const on = sel.region === id;
    return (
      <button
        key={id}
        type="button"
        onClick={() => setSel({ region: id, city: null })}
        aria-pressed={on}
        title={long || label}
        className={`inline-flex items-center gap-1.5 rounded-full border-2 font-bold whitespace-nowrap transition-colors ${compact ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm"} ${on ? "text-white shadow-md" : "bg-white text-[#1A1A1A] hover:text-white"}`}
        style={{ borderColor: color, backgroundColor: on ? color : undefined, "--hover": color }}
        onMouseEnter={(e) => { if (!on) e.currentTarget.style.backgroundColor = color; }}
        onMouseLeave={(e) => { if (!on) e.currentTarget.style.backgroundColor = ""; }}
        data-testid={`${testId}-${id}`}
      >
        {id === "all" ? <MapPin className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} /> : null}
        {label}
        {typeof count === "number" && <span className={`rounded-full px-1.5 text-[10px] font-extrabold ${on ? "bg-white/25" : "bg-black/5"}`}>{count}</span>}
      </button>
    );
  };

  const cityChips = active && active.id !== BEYOND.id ? visibleCities(active).map((c) => ({ name: c, n: cities[`${active.id}:${c.toLowerCase()}`] || 0 })).sort((a, b) => b.n - a.n || a.name.localeCompare(b.name)) : [];
  const withEvents = cityChips.filter((c) => c.n > 0);
  const quiet = cityChips.filter((c) => c.n === 0);

  return (
    <div className={compact ? "space-y-2" : "space-y-3"} data-testid={testId}>
      <div className="flex flex-wrap gap-2 items-center">
        {pill("all", compact ? "All" : "All South Sound", "#1A1A1A", counts.all, "Every region")}
        {regions.map((r) => pill(r.id, r.label, r.color || "#2E5C55", counts[r.id] || 0, r.long_label))}
        {counts[BEYOND.id] > 0 && pill(BEYOND.id, BEYOND.label, BEYOND.color, counts[BEYOND.id], BEYOND.long_label)}
        {extra}
      </div>

      {active && (
        <div className="rounded-2xl border-2 px-3 py-2.5 bg-white" style={{ borderColor: active.color }} data-testid={`${testId}-active`}>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <span className="inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-widest" style={{ color: active.color }} data-testid={`${testId}-viewing`}>
              <MapPin className="h-3.5 w-3.5" /> Viewing {sel.city || active.long_label || active.label} only
            </span>
            <button type="button" onClick={() => setSel({ region: "all", city: null })} className="inline-flex items-center gap-1 text-xs font-bold text-[#1A1A1A] underline underline-offset-2 hover:text-[#2E5C55]" data-testid={`${testId}-clear`}>
              <X className="h-3 w-3" /> Show all of the South Sound
            </button>
            {musicElsewhere > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] text-[#5C5C5C]" data-testid={`${testId}-music-note`}>
                <Music className="h-3 w-3" /> Music shows from the whole Sound stay in ({musicElsewhere}) — it's a short drive.
              </span>
            )}
          </div>
          {cityChips.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testId}-cities`}>
              <button type="button" onClick={() => setSel({ region: active.id, city: null })} aria-pressed={!sel.city}
                className={`rounded-full px-2.5 py-1 text-[11px] font-bold border transition-colors ${!sel.city ? "text-white" : "bg-white text-[#1A1A1A] border-[#E2E0DC] hover:border-[#1A1A1A]"}`}
                style={!sel.city ? { backgroundColor: active.color, borderColor: active.color } : undefined} data-testid={`${testId}-city-all`}>
                All {active.label}
              </button>
              {withEvents.map((c) => {
                const on = (sel.city || "").toLowerCase() === c.name.toLowerCase();
                return (
                  <button key={c.name} type="button" onClick={() => setSel({ region: active.id, city: on ? null : c.name })} aria-pressed={on}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-colors ${on ? "text-white" : "bg-white text-[#1A1A1A] border-[#E2E0DC] hover:border-[#1A1A1A]"}`}
                    style={on ? { backgroundColor: active.color, borderColor: active.color } : undefined} data-testid={`${testId}-city-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
                    {c.name} <span className="opacity-60">{c.n}</span>
                  </button>
                );
              })}
              {!compact && quiet.length > 0 && (
                <details className="inline">
                  <summary className="cursor-pointer rounded-full px-2.5 py-1 text-[11px] font-semibold text-[#5C5C5C] border border-dashed border-[#E2E0DC] list-none select-none">+{quiet.length} more towns (nothing listed yet)</summary>
                  <span className="inline-flex flex-wrap gap-1.5 mt-1.5">
                    {quiet.map((c) => <span key={c.name} className="rounded-full px-2.5 py-1 text-[11px] text-[#9A9A9A] border border-[#F0EEEA]">{c.name}</span>)}
                  </span>
                </details>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
